// CategoryModal.jsx (phosphor-react + revised UI, keeps your logic)
import React, { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { toast } from "sonner"
import axiosInstance from "../../utils/axios/axiosConfig"
import { X, Camera, UploadSimple, Trash, Plus, CheckSquare, Square } from "phosphor-react"
import { TextInput, TextArea } from "../../components"

const CategoryModal = ({
  isOpen,
  onClose,
  setCategoryToUpdate,
  CategoryToUpdate,
  categories,
  setDone,
  done,
}) => {
  const [categoryType, setCategoryType] = useState(CategoryToUpdate?.category_id ? "sub" : "main")
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    image: null,
    name: "",
    description: "",
    slug: "",
    category_id: "",
    featured: false,
  })

  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden"
    else document.body.style.overflow = "unset"
    return () => (document.body.style.overflow = "unset")
  }, [isOpen])

  useEffect(() => {
    if (isOpen && CategoryToUpdate?.id) {
      setFormData({
        image: CategoryToUpdate.image || null,
        name: CategoryToUpdate.name || "",
        description: CategoryToUpdate.description || "",
        slug: CategoryToUpdate.slug || "",
        category_id: CategoryToUpdate.category_id || "",
        featured: !!CategoryToUpdate.featured,
      })
      setCategoryType(CategoryToUpdate.category_id ? "sub" : "main")
    } else {
      setFormData({ image: null, name: "", description: "", slug: "", category_id: "", featured: false })
      setCategoryType("main")
    }
  }, [isOpen, CategoryToUpdate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      let response = null
      const submitData = { ...formData }

      if (CategoryToUpdate?.id) {
        response = CategoryToUpdate.category_id
          ? await axiosInstance.put(`/subcategories/update/${CategoryToUpdate.id}`, submitData)
          : await axiosInstance.put(`/categories/update/${CategoryToUpdate.id}`, submitData)
      } else {
        response =
          categoryType === "main"
            ? await axiosInstance.post("/categories/create", submitData)
            : await axiosInstance.post("/subcategories/create", submitData)
      }

      toast.success(response?.data?.message || "Saved")
      closeModal()
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong")
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target
    setFormData((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }))
  }

  const handleImageChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 15 * 1024 * 1024) {
      toast.warning("File size exceeds 15 MB limit")
      e.target.value = ""
      return
    }
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => setFormData((s) => ({ ...s, image: reader.result }))
  }

  const closeModal = () => {
    setFormData({ image: null, name: "", description: "", slug: "", category_id: "", featured: false })
    setCategoryToUpdate?.(null)
    setDone(!done)
    onClose?.()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/70" onClick={closeModal} />

          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25 }}
            className="relative ml-auto h-full w-full max-w-md bg-white dark:bg-neutral-900 border-l border-neutral-200 dark:border-neutral-800 overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-start justify-between gap-3 mb-6">
                <div className="text-start">
                  <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">
                    {CategoryToUpdate?.id ? "Update" : "Add"} Category
                  </h2>
                  <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                    Fill the details and save.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeModal}
                  className="h-10 w-10 inline-flex items-center justify-center rounded-xl border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                >
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Image */}
                <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-950/30 p-5">
                  <div className="text-start text-sm font-semibold text-neutral-900 dark:text-white">Image</div>

                  <div className="mt-4 flex flex-col items-center">
                    <div className="relative">
                      <div className="h-32 w-32 overflow-hidden rounded-full border-4 border-white dark:border-neutral-900">
                        {formData.image ? (
                          <img src={formData.image} alt="Category" className="h-full w-full object-cover" draggable={false} />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-neutral-100 dark:bg-neutral-800">
                            <Camera size={26} className="text-neutral-400" />
                          </div>
                        )}
                      </div>

                      <label
                        htmlFor="photo-upload"
                        className="absolute bottom-0 right-0 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-neutral-900 text-white hover:bg-neutral-700"
                      >
                        <UploadSimple size={18} />
                        <input
                          id="photo-upload"
                          type="file"
                          className="hidden"
                          onChange={handleImageChange}
                          accept="image/*"
                          name="image"
                        />
                      </label>
                    </div>

                    {formData.image && (
                      <button
                        type="button"
                        onClick={() => setFormData((s) => ({ ...s, image: null }))}
                        className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-rose-600 hover:text-rose-700"
                      >
                        <Trash size={18} />
                        Remove photo
                      </button>
                    )}
                    <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">PNG/JPG. Max 15MB.</p>
                  </div>
                </div>

                <TextInput
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. Skincare"
                  required
                />

                <TextInput
                  label="Slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  placeholder="e.g. skincare"
                  required
                />

                <TextArea
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Optional description..."
                />

                {/* Featured */}
                {categoryType === "main" && !CategoryToUpdate?.category_id && (
                  <label className="flex items-start gap-3 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-950/30 p-4 cursor-pointer">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={!!formData.featured}
                      onChange={handleChange}
                      className="peer absolute h-0 w-0 opacity-0"
                    />
                    <div className="mt-0.5 text-neutral-700 dark:text-neutral-200">
                      {formData.featured ? <CheckSquare size={20} /> : <Square size={20} />}
                    </div>
                    <div className="text-start">
                      <div className="text-sm font-semibold text-neutral-900 dark:text-white">
                        Feature this category on the homepage
                      </div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                        When enabled, this category appears on the homepage.
                      </div>
                    </div>
                  </label>
                )}

                {/* Type (only create) */}
                {!CategoryToUpdate?.id && (
                  <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-950/30 p-4">
                    <div className="text-start text-sm font-semibold text-neutral-900 dark:text-white">Category Type</div>

                    <div className="mt-3 flex flex-col sm:flex-row gap-3">
                      <label className="inline-flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
                        <input
                          type="radio"
                          value="main"
                          checked={categoryType === "main"}
                          onChange={() => setCategoryType("main")}
                          className="h-4 w-4"
                        />
                        Main Category
                      </label>

                      <label className="inline-flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
                        <input
                          type="radio"
                          value="sub"
                          checked={categoryType === "sub"}
                          onChange={() => setCategoryType("sub")}
                          className="h-4 w-4"
                        />
                        Subcategory
                      </label>
                    </div>
                  </div>
                )}

                {(categoryType === "sub" || CategoryToUpdate?.category_id) && (
                  <div className="text-start">
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-2">
                      Parent Category
                    </label>
                    <select
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleChange}
                      className="w-full h-11 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950/40 px-3 text-sm text-neutral-800 dark:text-neutral-100"
                      required={categoryType === "sub"}
                    >
                      <option value="">Select a parent category</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#2B2B35] rounded-xl hover:bg-cyan-400 disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <span className="animate-spin inline-block size-4 border-[2px] border-current border-t-transparent rounded-full" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Plus size={18} />
                        Save
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default CategoryModal
